import {
  createRootRouteWithContext,
  Outlet,
  ParsedLocation,
  redirect,
} from '@tanstack/react-router';
import { SubscriptionType, supabase } from '@upcaret/supabase-types';
import { AuthSession } from '@upcaret/ui/auth';
import { ErrorComponent } from '../error-component';

// const TanStackRouterDevtools = import.meta.env.DEV
//   ? React.lazy(() =>
//       // Lazy load in development
//       import('@tanstack/router-devtools').then((res) => ({
//         default: res.TanStackRouterDevtools,
//       }))
//     )
//   : () => null; // Render nothing in production

function isPWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches || // For most browsers
    (window.navigator as any).standalone || // For iOS
    document.referrer.includes('android-app://') // For TWA
  );
}

function hasValidSubscription(
  subscription: SubscriptionType | undefined | null,
) {
  if (!subscription) return false;

  return (
    ['active', 'trialing'].includes(subscription.status) &&
    new Date(subscription.current_period_end) > new Date()
  );
}

function pageTitle(location: ParsedLocation): string {
  if (location.pathname.startsWith('/app/chats/')) {
    return 'Chat';
  }
  switch (location.pathname) {
    case '/':
      return 'Cently';
    case '/features':
      return 'Features';
    case '/about':
      return 'About';
    case '/roadmap':
      return 'Road Map';
    case '/contact':
      return 'Contact';
    case '/welcome':
      return 'Welcome';
    case '/auth':
      return 'Sign In';
    case '/privacy':
      return 'Privacy Policy';
    case '/terms':
      return 'Terms of Service';
    case '/sign-out':
      return 'Sign Out';
    case '/app':
      return 'Dashboard';
    case '/app/receipts':
      return 'Receipts';
    case '/app/transactions':
      return 'Transactions';
    case '/app/chats':
      return 'Chat';
    case '/app/support':
      return 'Support';
    case '/app/settings':
      return 'Settings';
    default:
      return 'Cently';
  }
}

const fetchSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

const fetchUser = async () => {
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user ?? null;
  return user;
};

const fetchSubscription = async () => {
  const session = await fetchSession();
  if (!session) {
    return null;
  }

  const { data: subscriptionData } = await supabase
    .schema('api')
    .from('subscription')
    .select()
    .maybeSingle();
  return subscriptionData;
};

// Query configurations with proper stale times
const QUERY_CONFIGS = {
  session: {
    queryKey: ['session'],
    queryFn: fetchSession,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  },
  user: {
    queryKey: ['user'],
    queryFn: fetchUser,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  },
  subscription: {
    queryKey: ['subscription'],
    queryFn: fetchSubscription,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  },
} as const;

/**
 * Efficiently loads context data using TanStack Query best practices:
 * - Blocks when data isn't cached
 * - Loads async when data is fresh/cached
 * - Handles errors gracefully
 */
async function loadContextData(
  queryClient: any,
  keys: (keyof typeof QUERY_CONFIGS)[],
  context: any,
) {
  if (!queryClient) {
    throw new Error('Query client not available');
  }

  const loadPromises = keys.map(async (key) => {
    const config = QUERY_CONFIGS[key];

    // Check if we have fresh cached data
    const cachedData = queryClient.getQueryData(config.queryKey);
    const queryState = queryClient.getQueryState(config.queryKey);

    const isFresh =
      queryState &&
      queryState.dataUpdatedAt &&
      Date.now() - queryState.dataUpdatedAt < (config.staleTime || 0);

    if (cachedData && isFresh) {
      // Use cached data and prefetch in background
      context[key] = cachedData;
      queryClient.prefetchQuery(config);
      return cachedData;
    } else {
      // Block on loading fresh data
      try {
        const data = await queryClient.ensureQueryData(config);
        context[key] = data ?? null;
        return data;
      } catch (error) {
        console.error(`Failed to fetch ${key}:`, error);
        context[key] = null;
        return null;
      }
    }
  });

  await Promise.all(loadPromises);
}

export const Route = createRootRouteWithContext<AuthSession>()({
  beforeLoad: async ({ context, location }) => {
    context.pageTitle = pageTitle(location);

    // Handle sign out
    if (['/sign-out'].includes(location.pathname)) {
      await supabase.auth.signOut({ scope: 'local' });
      // Clear user context immediately to disable queries
      context.user = null;
      context.session = null;
      context.subscription = null;
      // Remove queries first to prevent refetches, then invalidate
      await context.queryClient?.removeQueries();
      await context.queryClient?.invalidateQueries();
      return context;
    }

    // Always load session and user data (required for auth checks)
    await loadContextData(context.queryClient, ['session', 'user'], context);

    const { user } = context;

    // Auth redirects (must happen after user data is loaded)
    if (user && location.pathname === '/') {
      throw redirect({ to: '/app' });
    }

    if (!user && location.pathname.startsWith('/app')) {
      throw redirect({
        to: '/auth',
        search: {
          redirect: !location.pathname.startsWith('/auth')
            ? location.href
            : '/app',
        },
      });
    }

    if (!user && location.pathname === '/' && isPWA()) {
      throw redirect({
        to: '/auth',
        search: { redirect: '/app' },
      });
    }

    if (user && location.pathname.startsWith('/auth')) {
      throw redirect({ to: '/app' });
    }

    // Handle initial password setup
    if (user && user.user_metadata && location.pathname !== '/app/settings') {
      const md = user.user_metadata;
      if (!md.initial_password_set) {
        throw redirect({
          to: '/app/settings',
          search: { nonce: new Date().getMilliseconds().toString() },
        });
      }
    }

    // Load subscription data based on route needs
    if (location.pathname === '/') {
      // Dashboard uses suspense - load subscription async (non-blocking)
      if (context.queryClient) {
        context.queryClient.prefetchQuery(QUERY_CONFIGS.subscription);
      }
    } else if (location.pathname.startsWith('/app')) {
      // App routes need subscription for access control - load synchronously
      await loadContextData(context.queryClient, ['subscription'], context);

      // Check subscription validity
      if (
        user &&
        !hasValidSubscription(context.subscription) &&
        location.pathname !== '/app/settings'
      ) {
        throw redirect({
          to: '/app/settings',
          search: { nonce: new Date().getMilliseconds().toString() },
        });
      }
    }

    return context;
  },
  loader: ({ context }) => {
    const { pageTitle } = context;
    document.title = pageTitle ?? 'Cently';
    return context;
  },

  component: () => {
    return (
      <>
        <Outlet />
        {/* <TanStackRouterDevtools position="bottom-right" initialIsOpen={false} /> */}
      </>
    );
  },
  errorComponent: ErrorComponent,
});
